import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Package, 
  PackagePlus,
  PackageMinus,
  Boxes,
  FolderOpen,
  TrendingUp,
  TrendingDown,
  Plus,
  RefreshCw,
  Search,
  Loader2,
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  Edit,
  Trash2,
  BarChart3,
  Send,
  RotateCcw,
  XCircle,
  FileWarning,
  Users,
  Calendar,
  UserCheck,
  Building,
  Undo2,
  ShieldAlert,
  FileX2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('items');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [stockEntries, setStockEntries] = useState([]);
  const [stockSummary, setStockSummary] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showStockEntryDialog, setShowStockEntryDialog] = useState(false);
  const [showItemHistoryDialog, setShowItemHistoryDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemHistory, setItemHistory] = useState(null);
  
  // Pagination state
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize] = useState(50);
  const [itemsPagination, setItemsPagination] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [itemForm, setItemForm] = useState({
    item_code: '',
    item_name: '',
    category_id: '',
    unit: 'pcs',
    description: '',
    minimum_stock: 0,
    reorder_level: 10,
    purchase_price: 0,
    selling_price: 0,
    hsn_code: '',
    tax_rate: 0
  });
  const [stockEntryForm, setStockEntryForm] = useState({
    item_id: '',
    entry_type: 'stock_in',
    quantity: '',
    unit_price: '',
    reference_no: '',
    remarks: '',
    supplier_name: '',
    issued_to: ''
  });

  // Issue Stock states
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [issueForm, setIssueForm] = useState({
    item_id: '',
    quantity: '',
    issued_to: '',
    issue_type: 'department',
    purpose: '',
    expected_return_date: '',
    remarks: ''
  });
  const [stockIssues, setStockIssues] = useState([]);

  // Damage/Loss states
  const [showDamageDialog, setShowDamageDialog] = useState(false);
  const [damageForm, setDamageForm] = useState({
    item_id: '',
    quantity: '',
    damage_type: 'damaged',
    damage_reason: '',
    damage_date: '',
    remarks: ''
  });
  const [stockDamages, setStockDamages] = useState([]);

  // Report states
  const [usageReport, setUsageReport] = useState(null);
  const [damageReport, setDamageReport] = useState(null);
  const [reportDateRange, setReportDateRange] = useState({
    from_date: new Date().toISOString().slice(0, 8) + '01',
    to_date: new Date().toISOString().slice(0, 10)
  });

  // Return stock dialog states
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnForm, setReturnForm] = useState({
    issueId: '',
    issuedQty: 0,
    returnedQty: 0,
    itemName: '',
    returnQuantity: ''
  });

  // Fetch functions
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/categories`, {
        headers: getAuthHeader()
      });
      setCategories(res.data);
    } catch (error) {
      if (error.response?.status === 404 || (Array.isArray(error.response?.data) && error.response?.data.length === 0)) {
        // Initialize defaults if empty
        await initializeCategories();
      }
    }
  };

  const initializeCategories = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/inventory/categories/initialize-defaults`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Default categories initialized');
      await fetchCategories();
    } catch (error) {
      if (!error.response?.data?.detail?.includes('already')) {
        toast.error('Failed to initialize categories');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category_id', selectedCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (showLowStock) {
        params.append('low_stock', 'true');
      }
      params.append('page', page.toString());
      params.append('page_size', itemsPageSize.toString());
      
      const res = await axios.get(`${API_URL}/api/inventory/items?${params}`, {
        headers: getAuthHeader()
      });
      setItems(res.data.items || []);
      setItemsPagination({
        total: res.data.total,
        page: res.data.page,
        total_pages: res.data.total_pages,
        has_next: res.data.has_next,
        has_prev: res.data.has_prev
      });
      setItemsPage(page);
    } catch (error) {
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const fetchStockEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/stock/entries`, {
        headers: getAuthHeader()
      });
      setStockEntries(res.data);
    } catch (error) {
      toast.error('Failed to fetch stock entries');
    }
  };

  const fetchStockSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/reports/stock-summary`, {
        headers: getAuthHeader()
      });
      setStockSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch stock summary');
    }
  };

  const fetchItemHistory = async (itemId) => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/stock/item/${itemId}`, {
        headers: getAuthHeader()
      });
      setItemHistory(res.data);
      setShowItemHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to fetch item history');
    }
  };

  // Fetch stock issues
  const fetchStockIssues = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/stock/issues`, {
        headers: getAuthHeader()
      });
      setStockIssues(res.data);
    } catch (error) {
      toast.error('Failed to fetch stock issues');
    }
  };

  // Fetch stock damages
  const fetchStockDamages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/stock/damages`, {
        headers: getAuthHeader()
      });
      setStockDamages(res.data);
    } catch (error) {
      toast.error('Failed to fetch damage records');
    }
  };

  // Fetch usage report
  const fetchUsageReport = async () => {
    try {
      const params = new URLSearchParams(reportDateRange);
      const res = await axios.get(`${API_URL}/api/inventory/reports/stock-usage?${params}`, {
        headers: getAuthHeader()
      });
      setUsageReport(res.data);
    } catch (error) {
      toast.error('Failed to fetch usage report');
    }
  };

  // Fetch damage report
  const fetchDamageReport = async () => {
    try {
      const params = new URLSearchParams(reportDateRange);
      const res = await axios.get(`${API_URL}/api/inventory/reports/damage-report?${params}`, {
        headers: getAuthHeader()
      });
      setDamageReport(res.data);
    } catch (error) {
      toast.error('Failed to fetch damage report');
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchStockSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'items') {
      fetchItems();
    } else if (activeTab === 'stock-entries') {
      fetchStockEntries();
    } else if (activeTab === 'issue-stock') {
      fetchStockIssues();
      if (items.length === 0) fetchItems();
    } else if (activeTab === 'damage-loss') {
      fetchStockDamages();
      if (items.length === 0) fetchItems(1);
    } else if (activeTab === 'reports') {
      fetchUsageReport();
      fetchDamageReport();
    }
  }, [activeTab, selectedCategory, showLowStock]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (activeTab === 'items') {
      fetchItems(1);
    }
  }, [searchQuery]);

  // Create handlers
  const handleCreateCategory = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/inventory/categories`, categoryForm, {
        headers: getAuthHeader()
      });
      toast.success('Category created');
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async () => {
    try {
      if (!itemForm.item_code || !itemForm.item_name) {
        toast.error('Item code and name are required');
        return;
      }
      setLoading(true);
      await axios.post(`${API_URL}/api/inventory/items`, itemForm, {
        headers: getAuthHeader()
      });
      toast.success('Item created');
      setShowItemDialog(false);
      setItemForm({
        item_code: '',
        item_name: '',
        category_id: '',
        unit: 'pcs',
        description: '',
        minimum_stock: 0,
        reorder_level: 10,
        purchase_price: 0,
        selling_price: 0,
        hsn_code: '',
        tax_rate: 0
      });
      fetchItems();
      fetchStockSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const handleStockEntry = async () => {
    try {
      if (!stockEntryForm.item_id || !stockEntryForm.quantity) {
        toast.error('Item and quantity are required');
        return;
      }
      setLoading(true);
      await axios.post(`${API_URL}/api/inventory/stock/entry`, {
        ...stockEntryForm,
        quantity: parseInt(stockEntryForm.quantity),
        unit_price: parseFloat(stockEntryForm.unit_price) || 0
      }, {
        headers: getAuthHeader()
      });
      toast.success(`Stock ${stockEntryForm.entry_type === 'stock_in' ? 'added' : 'removed'} successfully`);
      setShowStockEntryDialog(false);
      setStockEntryForm({
        item_id: '',
        entry_type: 'stock_in',
        quantity: '',
        unit_price: '',
        reference_no: '',
        remarks: '',
        supplier_name: '',
        issued_to: ''
      });
      fetchItems();
      fetchStockEntries();
      fetchStockSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create stock entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/inventory/items/${itemId}`, {
        headers: getAuthHeader()
      });
      toast.success('Item deleted');
      fetchItems();
      fetchStockSummary();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Quick stock actions
  const handleQuickStockIn = (item) => {
    setStockEntryForm({
      item_id: item.id,
      entry_type: 'stock_in',
      quantity: '',
      unit_price: item.purchase_price || '',
      reference_no: '',
      remarks: '',
      supplier_name: '',
      issued_to: ''
    });
    setSelectedItem(item);
    setShowStockEntryDialog(true);
  };

  const handleQuickStockOut = (item) => {
    setStockEntryForm({
      item_id: item.id,
      entry_type: 'stock_out',
      quantity: '',
      unit_price: item.purchase_price || '',
      reference_no: '',
      remarks: '',
      supplier_name: '',
      issued_to: ''
    });
    setSelectedItem(item);
    setShowStockEntryDialog(true);
  };

  // Handle stock issue
  const handleCreateIssue = async () => {
    try {
      if (!issueForm.item_id || !issueForm.quantity || !issueForm.issued_to) {
        toast.error('Item, quantity and issued to are required');
        return;
      }
      setLoading(true);
      await axios.post(`${API_URL}/api/inventory/stock/issue`, {
        ...issueForm,
        quantity: parseInt(issueForm.quantity)
      }, {
        headers: getAuthHeader()
      });
      toast.success('Stock issued successfully');
      setShowIssueDialog(false);
      setIssueForm({
        item_id: '',
        quantity: '',
        issued_to: '',
        issue_type: 'department',
        purpose: '',
        expected_return_date: '',
        remarks: ''
      });
      fetchStockIssues();
      fetchItems();
      fetchStockSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue stock');
    } finally {
      setLoading(false);
    }
  };

  // Handle stock return
  const handleReturnStock = async (issue) => {
    const remaining = issue.quantity - (issue.returned_quantity || 0);
    setReturnForm({
      issueId: issue.id,
      issuedQty: issue.quantity,
      returnedQty: issue.returned_quantity || 0,
      itemName: issue.item_name,
      returnQuantity: remaining.toString()
    });
    setShowReturnDialog(true);
  };

  const handleConfirmReturn = async () => {
    try {
      const returnQty = parseInt(returnForm.returnQuantity);
      if (!returnQty || returnQty <= 0) {
        toast.error('Please enter a valid quantity');
        return;
      }
      
      const remaining = returnForm.issuedQty - returnForm.returnedQty;
      if (returnQty > remaining) {
        toast.error(`Cannot return more than ${remaining} units`);
        return;
      }
      
      setLoading(true);
      await axios.put(`${API_URL}/api/inventory/stock/issues/${returnForm.issueId}/return?return_quantity=${returnQty}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Stock returned successfully');
      setShowReturnDialog(false);
      fetchStockIssues();
      fetchItems();
      fetchStockSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return stock');
    } finally {
      setLoading(false);
    }
  };

  // Handle stock damage
  const handleCreateDamage = async () => {
    try {
      if (!damageForm.item_id || !damageForm.quantity || !damageForm.damage_reason) {
        toast.error('Item, quantity and reason are required');
        return;
      }
      setLoading(true);
      await axios.post(`${API_URL}/api/inventory/stock/damage`, {
        ...damageForm,
        quantity: parseInt(damageForm.quantity)
      }, {
        headers: getAuthHeader()
      });
      toast.success('Damage recorded successfully');
      setShowDamageDialog(false);
      setDamageForm({
        item_id: '',
        quantity: '',
        damage_type: 'damaged',
        damage_reason: '',
        damage_date: '',
        remarks: ''
      });
      fetchStockDamages();
      fetchItems();
      fetchStockSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record damage');
    } finally {
      setLoading(false);
    }
  };

  // Quick issue stock action
  const handleQuickIssue = (item) => {
    setIssueForm({
      item_id: item.id,
      quantity: '',
      issued_to: '',
      issue_type: 'department',
      purpose: '',
      expected_return_date: '',
      remarks: ''
    });
    setSelectedItem(item);
    setShowIssueDialog(true);
  };

  // Quick damage action
  const handleQuickDamage = (item) => {
    setDamageForm({
      item_id: item.id,
      quantity: '',
      damage_type: 'damaged',
      damage_reason: '',
      damage_date: new Date().toISOString().slice(0, 10),
      remarks: ''
    });
    setSelectedItem(item);
    setShowDamageDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="inventory-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
            <p className="text-gray-500 mt-1">Manage school inventory, stock, and supplies</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCategoryDialog(true)}
              data-testid="add-category-btn"
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Add Category
            </Button>
            <Button 
              onClick={() => setShowItemDialog(true)}
              data-testid="add-item-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {stockSummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Items</p>
                    <p className="text-2xl font-bold">{stockSummary.total_items || 0}</p>
                  </div>
                  <Boxes className="h-10 w-10 text-blue-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Stock Value</p>
                    <p className="text-2xl font-bold">₹{(stockSummary.total_stock_value || 0).toLocaleString()}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className={stockSummary.low_stock_count > 0 ? "border-yellow-300 bg-yellow-50" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Low Stock Items</p>
                    <p className="text-2xl font-bold text-yellow-600">{stockSummary.low_stock_count || 0}</p>
                  </div>
                  <AlertTriangle className="h-10 w-10 text-yellow-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className={stockSummary.out_of_stock_count > 0 ? "border-red-300 bg-red-50" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Out of Stock</p>
                    <p className="text-2xl font-bold text-red-600">{stockSummary.out_of_stock_count || 0}</p>
                  </div>
                  <Package className="h-10 w-10 text-red-500 opacity-80" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 lg:w-[800px]">
            <TabsTrigger value="items" data-testid="items-tab">
              <Package className="h-4 w-4 mr-2" />
              Items
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab">
              <FolderOpen className="h-4 w-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="stock-entries" data-testid="entries-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              Entries
            </TabsTrigger>
            <TabsTrigger value="issue-stock" data-testid="issue-tab">
              <Send className="h-4 w-4 mr-2" />
              Issue
            </TabsTrigger>
            <TabsTrigger value="damage-loss" data-testid="damage-tab">
              <FileWarning className="h-4 w-4 mr-2" />
              Damage
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Items Tab */}
          <TabsContent value="items" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle>Inventory Items</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                        className="pl-9 w-[200px]"
                        data-testid="search-items"
                      />
                    </div>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant={showLowStock ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowLowStock(!showLowStock)}
                    >
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Low Stock
                    </Button>
                    <Button variant="outline" size="icon" onClick={fetchItems}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No items found. Add your first inventory item.</p>
                  </div>
                ) : (
                  <Table data-testid="items-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Item Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Stock</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map(item => (
                        <TableRow key={item.id} data-testid={`item-row-${item.id}`}>
                          <TableCell className="font-mono text-sm">{item.item_code}</TableCell>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.category_name || 'Uncategorized'}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span className={`font-semibold ${
                                item.current_stock === 0 ? 'text-red-600' :
                                item.current_stock <= item.reorder_level ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                {item.current_stock} {item.unit}
                              </span>
                              {item.current_stock <= item.reorder_level && item.current_stock > 0 && (
                                <Badge variant="warning" className="bg-yellow-100 text-yellow-700">Low</Badge>
                              )}
                              {item.current_stock === 0 && (
                                <Badge variant="destructive">Out</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">₹{(item.purchase_price || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium">₹{(item.stock_value || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleQuickStockIn(item)}
                                title="Stock In"
                              >
                                <ArrowUpCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleQuickStockOut(item)}
                                title="Stock Out"
                              >
                                <ArrowDownCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => fetchItemHistory(item.id)}
                                title="View History"
                              >
                                <BarChart3 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteItem(item.id)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                
                {/* Pagination */}
                {itemsPagination && itemsPagination.total_pages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {((itemsPage - 1) * itemsPageSize) + 1} - {Math.min(itemsPage * itemsPageSize, itemsPagination.total)} of {itemsPagination.total} items
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchItems(itemsPage - 1)}
                        disabled={!itemsPagination.has_prev}
                      >
                        Previous
                      </Button>
                      <span className="text-sm px-2">
                        Page {itemsPage} of {itemsPagination.total_pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchItems(itemsPage + 1)}
                        disabled={!itemsPagination.has_next}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Inventory Categories</CardTitle>
                  <Button onClick={() => setShowCategoryDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">No categories. Click to initialize defaults.</p>
                    <Button className="mt-4" onClick={initializeCategories}>
                      Initialize Default Categories
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.map(cat => (
                      <Card key={cat.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg">{cat.name}</h3>
                              <p className="text-sm text-gray-500 mt-1">{cat.description || 'No description'}</p>
                            </div>
                            <FolderOpen className="h-8 w-8 text-blue-500 opacity-60" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stock Entries Tab */}
          <TabsContent value="stock-entries" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Stock Entry History</CardTitle>
                  <Button onClick={() => setShowStockEntryDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Entry
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Voucher No</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockEntries.map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-mono text-sm">{entry.voucher_no}</TableCell>
                        <TableCell>{entry.entry_date}</TableCell>
                        <TableCell className="font-medium">{entry.item_name}</TableCell>
                        <TableCell>
                          <Badge variant={entry.entry_type === 'stock_in' ? 'default' : 'destructive'}>
                            {entry.entry_type === 'stock_in' ? (
                              <><ArrowUpCircle className="h-3 w-3 mr-1" /> In</>
                            ) : (
                              <><ArrowDownCircle className="h-3 w-3 mr-1" /> Out</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{entry.quantity}</TableCell>
                        <TableCell className="text-right">₹{(entry.unit_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium">₹{(entry.total_value || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-sm text-gray-500">{entry.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {stockEntries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          No stock entries found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-4">
            <div className="grid gap-4">
              {/* Low Stock Alert Card */}
              {stockSummary?.low_stock_items?.length > 0 && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-yellow-700">
                      <AlertTriangle className="h-5 w-5" />
                      Low Stock Alert
                    </CardTitle>
                    <CardDescription>Items below reorder level</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {stockSummary.low_stock_items.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-sm text-gray-500">{item.item_code}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-yellow-600">{item.current_stock}</p>
                            <p className="text-xs text-gray-400">Reorder: {item.reorder_level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Out of Stock Card */}
              {stockSummary?.out_of_stock_items?.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <Package className="h-5 w-5" />
                      Out of Stock
                    </CardTitle>
                    <CardDescription>Items with zero stock</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {stockSummary.out_of_stock_items.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-sm text-gray-500">{item.item_code}</p>
                          </div>
                          <Button size="sm" onClick={() => handleQuickStockIn(item)}>
                            <PackagePlus className="h-4 w-4 mr-1" />
                            Add Stock
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Category Summary */}
              {stockSummary?.category_summary && Object.keys(stockSummary.category_summary).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Category-wise Stock Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Items</TableHead>
                          <TableHead className="text-right">Total Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(stockSummary.category_summary).map(([category, data]) => (
                          <TableRow key={category}>
                            <TableCell className="font-medium">{category}</TableCell>
                            <TableCell className="text-right">{data.items}</TableCell>
                            <TableCell className="text-right">₹{(data.value || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Stock Usage Report */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" />
                        Stock Usage Report
                      </CardTitle>
                      <CardDescription>Who used how much stock</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={reportDateRange.from_date}
                        onChange={(e) => setReportDateRange({...reportDateRange, from_date: e.target.value})}
                        className="w-[150px]"
                      />
                      <Input
                        type="date"
                        value={reportDateRange.to_date}
                        onChange={(e) => setReportDateRange({...reportDateRange, to_date: e.target.value})}
                        className="w-[150px]"
                      />
                      <Button variant="outline" onClick={() => { fetchUsageReport(); fetchDamageReport(); }}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {usageReport ? (
                    <div className="space-y-4">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-600">Total Issues</p>
                          <p className="text-2xl font-bold">{usageReport.summary?.total_issues || 0}</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <p className="text-sm text-purple-600">Total Quantity</p>
                          <p className="text-2xl font-bold">{usageReport.summary?.total_quantity || 0}</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-600">Total Value</p>
                          <p className="text-2xl font-bold">₹{(usageReport.summary?.total_value || 0).toLocaleString()}</p>
                        </div>
                        <div className="p-4 bg-amber-50 rounded-lg">
                          <p className="text-sm text-amber-600">Returned</p>
                          <p className="text-2xl font-bold">{usageReport.summary?.total_returned || 0}</p>
                        </div>
                      </div>

                      {/* Usage by Recipient */}
                      {usageReport.usage_by_recipient?.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Issued To</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead className="text-right">Issues</TableHead>
                              <TableHead className="text-right">Quantity</TableHead>
                              <TableHead className="text-right">Value</TableHead>
                              <TableHead className="text-right">Returned</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {usageReport.usage_by_recipient.map((u, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{u.recipient}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{u.issue_type}</Badge>
                                </TableCell>
                                <TableCell className="text-right">{u.total_issues}</TableCell>
                                <TableCell className="text-right">{u.total_quantity}</TableCell>
                                <TableCell className="text-right">₹{(u.total_value || 0).toLocaleString()}</TableCell>
                                <TableCell className="text-right">{u.returned_quantity || 0}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-center py-4 text-gray-500">No usage data for selected period</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Damage Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileWarning className="h-5 w-5 text-red-600" />
                    Damage/Loss Report
                  </CardTitle>
                  <CardDescription>Stock damages, losses, and write-offs</CardDescription>
                </CardHeader>
                <CardContent>
                  {damageReport ? (
                    <div className="space-y-4">
                      {/* Summary */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-red-50 rounded-lg">
                          <p className="text-sm text-red-600">Total Records</p>
                          <p className="text-2xl font-bold">{damageReport.summary?.total_records || 0}</p>
                        </div>
                        <div className="p-4 bg-orange-50 rounded-lg">
                          <p className="text-sm text-orange-600">Quantity Lost</p>
                          <p className="text-2xl font-bold">{damageReport.summary?.total_quantity_lost || 0}</p>
                        </div>
                        <div className="p-4 bg-rose-50 rounded-lg">
                          <p className="text-sm text-rose-600">Total Loss Value</p>
                          <p className="text-2xl font-bold">₹{(damageReport.summary?.total_loss_value || 0).toLocaleString()}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">Damage Types</p>
                          <p className="text-2xl font-bold">{damageReport.summary?.damage_types || 0}</p>
                        </div>
                      </div>

                      {/* Damage by Type */}
                      {damageReport.damage_by_type?.length > 0 ? (
                        <div className="space-y-3">
                          {damageReport.damage_by_type.map((d, idx) => (
                            <Card key={idx} className="border-red-100">
                              <CardContent className="pt-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Badge variant={d.type === 'damaged' ? 'destructive' : d.type === 'lost' ? 'warning' : 'secondary'}>
                                      {d.type}
                                    </Badge>
                                    <span className="text-sm text-gray-500">{d.count} records</span>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-red-600">₹{(d.total_loss || 0).toLocaleString()}</p>
                                    <p className="text-xs text-gray-500">{d.total_quantity} units</p>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  {d.items?.slice(0, 3).map((item, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                      <span>{item.item_name} ({item.quantity})</span>
                                      <span className="text-gray-500">{item.date}</span>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center py-4 text-gray-500">No damage records for selected period</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Issue Stock Tab */}
          <TabsContent value="issue-stock" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="h-5 w-5 text-blue-600" />
                      Stock Issues
                    </CardTitle>
                    <CardDescription>Issue stock to departments, classes, or individuals</CardDescription>
                  </div>
                  <Button onClick={() => setShowIssueDialog(true)} data-testid="issue-stock-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Issue Stock
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table data-testid="issues-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Issue No</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Issued To</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockIssues.map(issue => (
                      <TableRow key={issue.id} data-testid={`issue-row-${issue.id}`}>
                        <TableCell className="font-mono text-sm">{issue.issue_no}</TableCell>
                        <TableCell>{issue.issue_date}</TableCell>
                        <TableCell className="font-medium">{issue.item_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <UserCheck className="h-4 w-4 text-gray-400" />
                            {issue.issued_to}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{issue.issue_type}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{issue.quantity} {issue.unit}</TableCell>
                        <TableCell className="text-right">₹{(issue.total_value || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={
                            issue.status === 'returned' ? 'success' : 
                            issue.status === 'partial_returned' ? 'warning' : 
                            'secondary'
                          } className={
                            issue.status === 'returned' ? 'bg-green-100 text-green-700' :
                            issue.status === 'partial_returned' ? 'bg-yellow-100 text-yellow-700' :
                            ''
                          }>
                            {issue.status === 'issued' ? 'Issued' : 
                             issue.status === 'partial_returned' ? `Partial (${issue.returned_quantity}/${issue.quantity})` :
                             'Returned'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {issue.status !== 'returned' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReturnStock(issue)}
                              data-testid={`return-btn-${issue.id}`}
                            >
                              <Undo2 className="h-4 w-4 mr-1" />
                              Return
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {stockIssues.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                          <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No stock issues found. Issue stock to departments or classes.</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Damage/Loss Tab */}
          <TabsContent value="damage-loss" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileWarning className="h-5 w-5 text-red-600" />
                      Damage & Loss Records
                    </CardTitle>
                    <CardDescription>Record damaged, expired, or lost stock</CardDescription>
                  </div>
                  <Button variant="destructive" onClick={() => setShowDamageDialog(true)} data-testid="record-damage-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Record Damage/Loss
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table data-testid="damages-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Damage No</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Loss Value</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockDamages.map(damage => (
                      <TableRow key={damage.id} data-testid={`damage-row-${damage.id}`}>
                        <TableCell className="font-mono text-sm">{damage.damage_no}</TableCell>
                        <TableCell>{damage.damage_date}</TableCell>
                        <TableCell className="font-medium">{damage.item_name}</TableCell>
                        <TableCell>
                          <Badge variant={
                            damage.damage_type === 'damaged' ? 'destructive' :
                            damage.damage_type === 'expired' ? 'warning' :
                            damage.damage_type === 'lost' ? 'secondary' :
                            'outline'
                          } className={
                            damage.damage_type === 'expired' ? 'bg-amber-100 text-amber-700' : ''
                          }>
                            {damage.damage_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{damage.quantity} {damage.unit}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          ₹{(damage.loss_value || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">
                          {damage.damage_reason}
                        </TableCell>
                      </TableRow>
                    ))}
                    {stockDamages.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          <FileWarning className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No damage records found.</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Category Dialog */}
        <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Inventory Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category Name *</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="e.g., Stationery, Electronics"
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateCategory} disabled={loading || !categoryForm.name}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Item Dialog */}
        <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Inventory Item</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Item Code *</Label>
                <Input
                  value={itemForm.item_code}
                  onChange={(e) => setItemForm({ ...itemForm, item_code: e.target.value })}
                  placeholder="e.g., STN001"
                  data-testid="item-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={itemForm.item_name}
                  onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })}
                  placeholder="e.g., Whiteboard Marker"
                  data-testid="item-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={itemForm.category_id}
                  onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select
                  value={itemForm.unit}
                  onValueChange={(v) => setItemForm({ ...itemForm, unit: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">Pieces (pcs)</SelectItem>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="ltr">Liters (ltr)</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                    <SelectItem value="set">Set</SelectItem>
                    <SelectItem value="dozen">Dozen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Purchase Price</Label>
                <Input
                  type="number"
                  value={itemForm.purchase_price}
                  onChange={(e) => setItemForm({ ...itemForm, purchase_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Selling Price</Label>
                <Input
                  type="number"
                  value={itemForm.selling_price}
                  onChange={(e) => setItemForm({ ...itemForm, selling_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Reorder Level</Label>
                <Input
                  type="number"
                  value={itemForm.reorder_level}
                  onChange={(e) => setItemForm({ ...itemForm, reorder_level: parseInt(e.target.value) || 0 })}
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label>Minimum Stock</Label>
                <Input
                  type="number"
                  value={itemForm.minimum_stock}
                  onChange={(e) => setItemForm({ ...itemForm, minimum_stock: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Input
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                  placeholder="Item description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateItem} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stock Entry Dialog */}
        <Dialog open={showStockEntryDialog} onOpenChange={setShowStockEntryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {stockEntryForm.entry_type === 'stock_in' ? (
                  <span className="flex items-center gap-2 text-green-700">
                    <PackagePlus className="h-5 w-5" /> Stock In
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-red-700">
                    <PackageMinus className="h-5 w-5" /> Stock Out
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedItem && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{selectedItem.item_name}</p>
                  <p className="text-sm text-gray-500">Current Stock: {selectedItem.current_stock} {selectedItem.unit}</p>
                </div>
              )}
              
              {!selectedItem && (
                <div className="space-y-2">
                  <Label>Select Item *</Label>
                  <Select
                    value={stockEntryForm.item_id}
                    onValueChange={(v) => setStockEntryForm({ ...stockEntryForm, item_id: v })}
                  >
                    <SelectTrigger data-testid="select-item-stock">
                      <SelectValue placeholder="Choose item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.item_name} ({item.current_stock} {item.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Entry Type</Label>
                <Select
                  value={stockEntryForm.entry_type}
                  onValueChange={(v) => setStockEntryForm({ ...stockEntryForm, entry_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock_in">Stock In (Purchase/Return)</SelectItem>
                    <SelectItem value="stock_out">Stock Out (Issue/Sale)</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    value={stockEntryForm.quantity}
                    onChange={(e) => setStockEntryForm({ ...stockEntryForm, quantity: e.target.value })}
                    placeholder="0"
                    data-testid="stock-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    value={stockEntryForm.unit_price}
                    onChange={(e) => setStockEntryForm({ ...stockEntryForm, unit_price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {stockEntryForm.entry_type === 'stock_in' && (
                <div className="space-y-2">
                  <Label>Supplier Name</Label>
                  <Input
                    value={stockEntryForm.supplier_name}
                    onChange={(e) => setStockEntryForm({ ...stockEntryForm, supplier_name: e.target.value })}
                    placeholder="Supplier name"
                  />
                </div>
              )}

              {stockEntryForm.entry_type === 'stock_out' && (
                <div className="space-y-2">
                  <Label>Issued To</Label>
                  <Input
                    value={stockEntryForm.issued_to}
                    onChange={(e) => setStockEntryForm({ ...stockEntryForm, issued_to: e.target.value })}
                    placeholder="Department/Person"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Reference No.</Label>
                <Input
                  value={stockEntryForm.reference_no}
                  onChange={(e) => setStockEntryForm({ ...stockEntryForm, reference_no: e.target.value })}
                  placeholder="Invoice/PO number"
                />
              </div>

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={stockEntryForm.remarks}
                  onChange={(e) => setStockEntryForm({ ...stockEntryForm, remarks: e.target.value })}
                  placeholder="Additional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowStockEntryDialog(false);
                setSelectedItem(null);
              }}>Cancel</Button>
              <Button 
                onClick={handleStockEntry} 
                disabled={loading}
                className={stockEntryForm.entry_type === 'stock_in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {stockEntryForm.entry_type === 'stock_in' ? 'Add Stock' : 'Remove Stock'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Item History Dialog */}
        <Dialog open={showItemHistoryDialog} onOpenChange={setShowItemHistoryDialog}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Item Stock History</DialogTitle>
            </DialogHeader>
            {itemHistory && (
              <div className="space-y-4">
                {/* Item Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-gray-500">Current Stock</p>
                      <p className="text-2xl font-bold">{itemHistory.item?.current_stock || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-gray-500">Total In</p>
                      <p className="text-2xl font-bold text-green-600">{itemHistory.summary?.total_stock_in || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-gray-500">Total Out</p>
                      <p className="text-2xl font-bold text-red-600">{itemHistory.summary?.total_stock_out || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-gray-500">Stock Value</p>
                      <p className="text-2xl font-bold">₹{(itemHistory.summary?.stock_value || 0).toLocaleString()}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* History Table */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Voucher</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemHistory.entries?.map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.entry_date}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.voucher_no}</TableCell>
                        <TableCell>
                          <Badge variant={entry.entry_type === 'stock_in' ? 'default' : 'destructive'}>
                            {entry.entry_type === 'stock_in' ? 'In' : 'Out'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{entry.quantity}</TableCell>
                        <TableCell className="text-right font-medium">{entry.stock_after}</TableCell>
                        <TableCell className="text-sm text-gray-500">{entry.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Issue Stock Dialog */}
        <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-700">
                <Send className="h-5 w-5" /> Issue Stock
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedItem && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium">{selectedItem.item_name}</p>
                  <p className="text-sm text-blue-600">Available Stock: {selectedItem.current_stock} {selectedItem.unit}</p>
                </div>
              )}
              
              {!selectedItem && (
                <div className="space-y-2">
                  <Label>Select Item *</Label>
                  <Select
                    value={issueForm.item_id}
                    onValueChange={(v) => setIssueForm({ ...issueForm, item_id: v })}
                  >
                    <SelectTrigger data-testid="select-item-issue">
                      <SelectValue placeholder="Choose item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.filter(i => i.current_stock > 0).map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.item_name} ({item.current_stock} {item.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    value={issueForm.quantity}
                    onChange={(e) => setIssueForm({ ...issueForm, quantity: e.target.value })}
                    placeholder="0"
                    data-testid="issue-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Issue Type</Label>
                  <Select
                    value={issueForm.issue_type}
                    onValueChange={(v) => setIssueForm({ ...issueForm, issue_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="department">Department</SelectItem>
                      <SelectItem value="class">Class</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Issued To *</Label>
                <Input
                  value={issueForm.issued_to}
                  onChange={(e) => setIssueForm({ ...issueForm, issued_to: e.target.value })}
                  placeholder="Department name, Class, or Person"
                  data-testid="issued-to-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Purpose</Label>
                <Input
                  value={issueForm.purpose}
                  onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })}
                  placeholder="What will it be used for?"
                />
              </div>

              <div className="space-y-2">
                <Label>Expected Return Date (optional)</Label>
                <Input
                  type="date"
                  value={issueForm.expected_return_date}
                  onChange={(e) => setIssueForm({ ...issueForm, expected_return_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={issueForm.remarks}
                  onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })}
                  placeholder="Additional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowIssueDialog(false);
                setSelectedItem(null);
              }}>Cancel</Button>
              <Button 
                onClick={handleCreateIssue} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-issue-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Issue Stock
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Damage/Loss Dialog */}
        <Dialog open={showDamageDialog} onOpenChange={setShowDamageDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <FileWarning className="h-5 w-5" /> Record Damage/Loss
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedItem && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="font-medium">{selectedItem.item_name}</p>
                  <p className="text-sm text-red-600">Current Stock: {selectedItem.current_stock} {selectedItem.unit}</p>
                </div>
              )}
              
              {!selectedItem && (
                <div className="space-y-2">
                  <Label>Select Item *</Label>
                  <Select
                    value={damageForm.item_id}
                    onValueChange={(v) => setDamageForm({ ...damageForm, item_id: v })}
                  >
                    <SelectTrigger data-testid="select-item-damage">
                      <SelectValue placeholder="Choose item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.filter(i => i.current_stock > 0).map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.item_name} ({item.current_stock} {item.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    value={damageForm.quantity}
                    onChange={(e) => setDamageForm({ ...damageForm, quantity: e.target.value })}
                    placeholder="0"
                    data-testid="damage-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Damage Type</Label>
                  <Select
                    value={damageForm.damage_type}
                    onValueChange={(v) => setDamageForm({ ...damageForm, damage_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="damaged">Damaged</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                      <SelectItem value="broken">Broken</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Damage Date</Label>
                <Input
                  type="date"
                  value={damageForm.damage_date}
                  onChange={(e) => setDamageForm({ ...damageForm, damage_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Reason *</Label>
                <Input
                  value={damageForm.damage_reason}
                  onChange={(e) => setDamageForm({ ...damageForm, damage_reason: e.target.value })}
                  placeholder="Describe what happened"
                  data-testid="damage-reason-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={damageForm.remarks}
                  onChange={(e) => setDamageForm({ ...damageForm, remarks: e.target.value })}
                  placeholder="Additional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowDamageDialog(false);
                setSelectedItem(null);
              }}>Cancel</Button>
              <Button 
                variant="destructive"
                onClick={handleCreateDamage} 
                disabled={loading}
                data-testid="confirm-damage-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileWarning className="h-4 w-4 mr-2" />}
                Record Damage
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Return Stock Dialog */}
        <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <Undo2 className="h-5 w-5" /> Return Stock
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="font-medium">{returnForm.itemName}</p>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-600">Issued Quantity:</span>
                  <span className="font-semibold">{returnForm.issuedQty}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Already Returned:</span>
                  <span className="font-semibold">{returnForm.returnedQty}</span>
                </div>
                <div className="flex justify-between text-sm text-green-700 font-medium">
                  <span>Remaining to Return:</span>
                  <span>{returnForm.issuedQty - returnForm.returnedQty}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Quantity to Return *</Label>
                <Input
                  type="number"
                  value={returnForm.returnQuantity}
                  onChange={(e) => setReturnForm({ ...returnForm, returnQuantity: e.target.value })}
                  max={returnForm.issuedQty - returnForm.returnedQty}
                  min={1}
                  placeholder="Enter quantity"
                  data-testid="return-quantity-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReturnDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmReturn} 
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-return-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Undo2 className="h-4 w-4 mr-2" />}
                Return Stock
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default InventoryPage;
